import { takeLatest, put, call, select } from 'redux-saga/effects';
import axios from 'axios';

// constants for endpoints
import { AUTHENTICATION } from '../../../components/global/Server/endpoints';

// constants for actions
import {
  AUTH_LOGIN,
  AUTH_LOGOUT,
  GET_EMPLOYEES,
  GET_ONLINE_EMPLOYEES,
  GET_USERS,
  REFRESH_USER_INFO,
  REGISTER_USER,
} from '../../constants/auth';
import {
  DELETE_RFID,
  PROCESS_RFID,
  REGISTER_RFID,
} from '../../constants/userManagement';

// secondary actions
import {
  requestStatus,
  clearStatus,
  showErrorMessage,
  hideError,
  showLicenseDialog,
} from '../../actions/global';
import { fetchLinkedUsers, storeRFID } from '../../actions/userManagement';
import { listAvailableCompanies } from '../../actions/settings';
import {
  setLoginCaptchaRequired,
  storeEmployees,
  storeOnlineEmployees,
  storeToken,
  storeUsers,
  updateUserInfo,
} from '../../actions/auth';
import { HTTP_SUCCESS } from '../../constants/global';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import AuthUtil from '../../AuthUtil';

function* getUserInfo(data, token) {
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  // Stamp the currently-selected company so the backend's
  // AccountService.getUserInfo evaluates lockState against the company
  // the user is operating in, not their home company. Without this, an
  // authoritative refresh after a 423 (refreshUserInfoSaga) reads the
  // home company's lockState and unmounts the LockedShell that the
  // optimistic 423 path just opened.
  const selectedCompanyId =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('selectedCompanyId')
      : '';
  if (selectedCompanyId) {
    headers['X-Selected-Company'] = selectedCompanyId;
  }
  return yield axios
    .post(AUTHENTICATION.GET_USER_INFO, data, { headers })
    .then((response) => response.data);
}

function* request(username, password, recaptchaToken) {
  return yield axios
    .post(AUTHENTICATION.AUTH_LOGIN, {
      username,
      password,
      // Invisible reCAPTCHA v3 token. null/omitted when reCAPTCHA isn't
      // configured; backend only enforces it after repeated failures.
      recaptchaToken,
    })
    .then((res) => res.data)
    .catch((error) => {
      let response = {};
      if (error.response) {
        response.status = error.response.status;
        response.statusText = error.response.statusText;
        response.data = error.response.data;
      }
      return response;
    });
}

function* authLogout() {
  yield put(hideError());
  try {
    yield call(() => AuthUtil.postRequest(AUTHENTICATION.AUTH_LOGOUT, {}));
    yield localStorage.clear();
    window.location.replace('/login');
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* authLogin(action) {
  yield put(hideError());
  // Wipe any stale linked-company selection from a previous user's
  // session on this browser. Without this, the first user-info / feature
  // request after login would stamp the previous user's selectedCompanyId
  // onto the X-Selected-Company header — the backend would safely fall
  // back to the home company (the helper validates membership), but it
  // pollutes logs and confuses anyone debugging.
  localStorage.removeItem('selectedCompanyId');
  try {
    const response = yield call(() =>
      request(action.username, action.password, action.recaptchaToken)
    );
    if (response.access_token) {
      const additionalInfo = yield call(() =>
        getUserInfo({ username: action.username }, response.access_token)
      );

      localStorage.setItem('token', response.access_token);
      // Seed the selected company to the user's home so the very next
      // request after login carries a valid X-Selected-Company header.
      // CompanyPicker overwrites this on switch.
      if (additionalInfo?.companyId) {
        localStorage.setItem('selectedCompanyId', additionalInfo.companyId);
      }
      // Mirror the access mode next to the selected company so AuthUtil —
      // a static class outside the React tree — can refuse writes at a
      // view-only location without a round-trip. Same storage pattern and
      // same lifetime as selectedCompanyId; handleLogout clears both.
      localStorage.setItem('accessMode', additionalInfo?.accessMode || 'FULL');

      const userInfo = {
        username: response.username,
        authority: response.roles,
        firstname: additionalInfo.firstname,
        lastname: additionalInfo.lastname,
        userId: additionalInfo.userId,
        company: additionalInfo.companyId,
        linkedUsers: additionalInfo.linkedUsers,
        routes: additionalInfo.routes,
        // Whether this employee has an OPEN shift right now, recomputed
        // server-side from ClockRecord. `routes` above is what they are
        // GRANTED; this is what they can reach until they clock in. Seeded at
        // login so the very first render after sign-in is already gated —
        // see constants/clockInAccess.js.
        isClockedIn: additionalInfo.isClockedIn,
        // Stamped by the RFID tap. Together with isClockedIn these are the
        // three inputs to routesForShiftState; seeded here so the first render
        // after sign-in already knows whether the chip has been tapped, rather
        // than guessing until /account/getUsers returns.
        isUserOnline: additionalInfo.isUserOnline,
        sessionId: additionalInfo.sessionId,
        userType: additionalInfo.userType,
        employeeColor: additionalInfo.employeeColor,
        companyIds: additionalInfo.companyIds,
        selectedLocations: additionalInfo.selectedLocations,
        defaultLocationId: additionalInfo.defaultLocationId,
        selectedLocationId: additionalInfo.companyId,
        isOwner: additionalInfo.isOwner,
        selectedLocationName: additionalInfo.selectedLocationName,
        planName: additionalInfo.planName,
        // Single source of truth for "is this user locked". Backend
        // builds this from company.enabled / status / revokedReason and
        // the user's roles. Drives <LockedShell /> in Base.jsx and is
        // refreshed on every 423 response.
        lockState: additionalInfo.lockState || null,
        // Per-location access mode ('FULL' | 'VIEW'), resolved server-side
        // for the company this call was evaluated against. Drives the
        // read-only banner and every <WriteGuard> in the app. Defaults to
        // FULL so a backend that predates the field behaves exactly as
        // before.
        accessMode: additionalInfo.accessMode || 'FULL',
      };

      yield put(storeToken(response.access_token, userInfo));
      // Successful login — clear any captcha requirement.
      yield put(setLoginCaptchaRequired(false));

      // Define who counts as an Admin
      const isAdmin =
        response.roles.includes('ROLE_ADMIN') ||
        response.roles.includes('ROLE_MASTER_ADMIN');

      // A user is a *dedicated* security person only if their role
      // bundle is the Security preset — i.e. they have ROLE_SECURITY
      // but not the operational dashboard roles that Manager / Super-
      // visor / Employee always get (ROLE_CUSTOMER from the Dashboard
      // section). Without this check, Managers and Supervisors who
      // have the Security section enabled would also be redirected
      // to /security on login.
      const hasSecurityRole = response.roles.includes('ROLE_SECURITY');
      const hasCustomerAccess = response.roles.includes('ROLE_CUSTOMER');
      const isDedicatedSecurity = hasSecurityRole && !hasCustomerAccess;

      if (isDedicatedSecurity && !isAdmin) {
        // Dedicated Security role: land on the security dashboard.
        yield action.history.push('/security');
      } else if (isAdmin) {
        // Admins go to the main dashboard/home even if they have the security role
        yield action.history.push('/');
      } else {
        // Everyone else
        yield action.history.push('/');
      }
    } else {
      // Failure response. Body is plain text for the legacy company-expiry
      // case and JSON ({message, captchaRequired}) for the reCAPTCHA cases.
      const data = response?.data;

      if (
        typeof data === 'string' &&
        data.includes('Your company account has expired.')
      ) {
        yield put(showLicenseDialog());
      } else {
        // Bad credentials / captcha needed. We DON'T pop a snackbar — the
        // login form's inline alert handles the "Login failed" message. We
        // only toggle the captcha checkbox based on the backend flag.
        yield put(setLoginCaptchaRequired(!!(data && data.captchaRequired)));
      }
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* registerUser(action) {
  yield call(clearStatus);

  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.AUTH_REGISTER, action.newUser)
    );

    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === 200,
    };

    yield put(requestStatus(status));
    if (status.success && action.newUser.is_linked_user) {
      const userAction = {
        locationId: action.newUser.selectedLocationId,
      };
      yield put(fetchLinkedUsers(userAction));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getUsersSaga() {
  try {
    const response = yield call(() =>
      AuthUtil.getRequest(AUTHENTICATION.GET_USERS)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeUsers(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getEmployeesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.GET_EMPLOYEES, action.data)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeEmployees(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getOnlineEmployeesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.GET_ONLINE_EMPLOYEES, action.data)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeOnlineEmployees(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* registerRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.REGISTER_RFID, action.data)
    );
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data.message);
      }
      yield put(storeRFID(response.data.user));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* processRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.PROCESS_RFID, action.data)
    );
    if (action.callback && typeof action.callback === 'function') {
      action.callback(response.data.success, response.data.message);
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.DELETE_RFID, action.data)
    );
    if (response.data.success) {
      yield put(storeRFID(response.data.user));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* refreshUserInfoSaga() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    const currentUserInfo = yield select((state) => state.auth?.userInfo);
    const username = currentUserInfo?.username;
    if (!username) return;
    const additionalInfo = yield call(() => getUserInfo({ username }, token));
    if (!additionalInfo) return;
    // Keep AuthUtil's copy in step with redux — an owner flipping a
    // location to View Only takes effect on the next refresh.
    localStorage.setItem('accessMode', additionalInfo.accessMode || 'FULL');
    // UPDATE_USER_INFO replaces the userInfo object outright, so merge
    // with the current state to avoid wiping fields the saga doesn't
    // know about (companyIds, selectedLocationName, etc.).
    // lockState is included so a periodic refresh transitions the user
    // to / from the locked shell in-place without requiring re-login.
    // routes is refreshed so a live change to the granted route list
    // (e.g. toggling the per-location NuVe Boost rule) shows/hides its
    // sidebar item immediately without a re-login or location switch.
    // Which locations this account can reach. Refreshed for the same
    // reason as routes: an access request approved by another company's
    // owner changes these server-side, and without picking them up here
    // the newly granted location stays invisible until the user logs out
    // and back in — the grant is real, the picker just never hears
    // about it.
    const nextCompanyIds =
      additionalInfo.companyIds || currentUserInfo.companyIds;
    const nextSelectedLocations =
      additionalInfo.selectedLocations || currentUserInfo.selectedLocations;
    const locationsChanged =
      JSON.stringify(nextCompanyIds || []) !==
        JSON.stringify(currentUserInfo.companyIds || []) ||
      JSON.stringify(nextSelectedLocations || []) !==
        JSON.stringify(currentUserInfo.selectedLocations || []);

    yield put(
      updateUserInfo({
        ...currentUserInfo,
        planName: additionalInfo.planName,
        lockState: additionalInfo.lockState || null,
        routes: additionalInfo.routes || currentUserInfo.routes,
        // Clock state, recomputed server-side from ClockRecord on every
        // getUser. `routes` above is the GRANTED set and knows nothing about
        // shifts, so without this the refresh would hand a clocked-out
        // employee their full menu back — which is exactly what let a typed
        // /ticket-out URL open. The shift gate reads this; see
        // constants/clockInAccess.js.
        isClockedIn:
          additionalInfo.isClockedIn !== undefined
            ? additionalInfo.isClockedIn
            : currentUserInfo.isClockedIn,
        // The other two inputs to that gate. isUserOnline and sessionId are
        // stamped by the RFID tap (processRFID), and routesForShiftState needs
        // all three together — refreshing isClockedIn alone would leave a
        // tapped-in employee looking un-tapped and bounce them to the "scan
        // your Chip" screen mid-shift.
        isUserOnline:
          additionalInfo.isUserOnline !== undefined
            ? additionalInfo.isUserOnline
            : currentUserInfo.isUserOnline,
        sessionId:
          additionalInfo.sessionId !== undefined
            ? additionalInfo.sessionId
            : currentUserInfo.sessionId,
        // Refreshed alongside routes so an owner flipping a location from
        // Full Control to View Only takes effect on the next refresh
        // rather than waiting for a re-login.
        accessMode: additionalInfo.accessMode || 'FULL',
        companyIds: nextCompanyIds,
        selectedLocations: nextSelectedLocations,
      })
    );

    // The picker renders from its own fetched list, not from userInfo, so
    // a changed grant needs that list pulled again or the new location
    // still will not appear in the dropdown.
    if (locationsChanged) {
      yield put(listAvailableCompanies());
    }

    // ACCESS TAKEN AWAY WHILE STANDING IN IT.
    //
    // A user viewing a linked location whose access was just revoked is
    // now parked on a company they no longer hold. The server already
    // refuses the stale X-Selected-Company header and quietly answers as
    // their HOME company, which is worse than an error: the header still
    // reads the other location while the data underneath belongs to their
    // own, so they are reading one gameroom's numbers under another
    // gameroom's name.
    //
    // Send them home instead — same mechanics the picker uses for a
    // deliberate switch (stamp the id and mode, then reload) so every
    // screen drops the revoked location's data rather than half of it.
    const home = additionalInfo.companyId || currentUserInfo.companyId;
    const allowed = [
      home,
      ...(nextCompanyIds || []),
      ...(nextSelectedLocations || []),
    ].filter(Boolean);
    const viewing =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('selectedCompanyId')
        : null;

    // BOTH copies of "which company am I in", because they drift apart.
    // localStorage feeds the X-Selected-Company header; userInfo feeds
    // the `locationId` that screens put in their request BODIES. Fixing
    // only the header leaves every screen still asking for the revoked
    // company by id — the header says home, the body says the old site,
    // and the old site is what comes back.
    const stale = [viewing, currentUserInfo.selectedLocationId]
      .filter(Boolean)
      .some((id) => !allowed.includes(id));

    if (stale && home) {
      localStorage.setItem('selectedCompanyId', home);
      // Home is always FULL — LocationAccess refuses to put anyone in
      // view-only mode at their own company.
      localStorage.setItem('accessMode', 'FULL');
      yield put(
        updateUserInfo({
          ...currentUserInfo,
          companyIds: nextCompanyIds,
          selectedLocations: nextSelectedLocations,
          selectedLocationId: home,
          selectedLocationName:
            additionalInfo.selectedLocationName ||
            currentUserInfo.selectedLocationName,
          accessMode: 'FULL',
        })
      );
      if (typeof window !== 'undefined') {
        setTimeout(() => window.location.reload(), 100);
      }
    }
  } catch (e) {
    // Silent — a missed refresh just means the banner shows stale state
    // until the next login.
  }
}

export default function* authSagas() {
  yield takeLatest(AUTH_LOGIN, authLogin);
  yield takeLatest(REFRESH_USER_INFO, refreshUserInfoSaga);
  yield takeLatest(REGISTER_USER, registerUser);
  yield takeLatest(AUTH_LOGOUT, authLogout);
  yield takeLatest(GET_USERS, getUsersSaga);
  yield takeLatest(REGISTER_RFID, registerRFIDSaga);
  yield takeLatest(DELETE_RFID, deleteRFIDSaga);
  yield takeLatest(PROCESS_RFID, processRFIDSaga);
  yield takeLatest(GET_EMPLOYEES, getEmployeesSaga);
  yield takeLatest(GET_ONLINE_EMPLOYEES, getOnlineEmployeesSaga);
  // NOTE: UPLOAD_BONUS_PHOTO is handled ONLY by the queue saga in
  // sagas/settings. A second watcher here shared the same action type and
  // double-uploaded every bonus photo.
}
